import { AskableIcon } from '@askable/ui/core/askable-icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@askable/ui/core/tooltip';
import { cn } from '@askable/ui/lib/utils';
import { PanelLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { utils } from 'lib/utils';

type SidebarToggleProps = {
  isCollapsed: boolean;
  onToggle: () => void;
};

export const SidebarToggle = ({ isCollapsed, onToggle }: SidebarToggleProps) => {
  const { t } = useTranslation();
  const [hoverEnabled, setHoverEnabled] = useState(false);

  // Wait for the sidebar to be collapsed before enabling the hover effect
  // Prevents the icon flashing while collapsing
  useEffect(() => {
    if (isCollapsed) {
      const timer = setTimeout(() => setHoverEnabled(true), 150);
      return () => clearTimeout(timer);
    } else {
      setHoverEnabled(false);
    }
  }, [isCollapsed]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {isCollapsed ? (
          <button
            type="button"
            className="group/logo ml-1 grid aspect-square h-7 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-foreground/[0.04] focus-visible:bg-foreground/[0.04] active:bg-foreground/[0.065]"
            aria-label={t('sections.navigation.sidebarShow')}
            onClick={onToggle}
          >
            {/* The mark and the toggle swap on hover to show what the rail does, but
                they cut rather than cross-fade — the mark is the brand sitting still,
                not something to animate. */}
            <div
              className={cn(
                `col-start-1 col-end-2 row-start-1 row-end-2 m-auto group-hover/logo:opacity-0 group-focus-visible/logo:opacity-0`,
                { 'group-hover/sidebar:opacity-0': hoverEnabled },
              )}
            >
              <AskableIcon className="h-5 w-5 text-brand" />
            </div>

            <div
              className={cn(
                `col-start-1 col-end-2 row-start-1 row-end-2 m-auto opacity-0 group-hover/logo:opacity-100 group-focus-visible/logo:opacity-100`,
                { 'group-hover/sidebar:opacity-100': hoverEnabled },
              )}
            >
              <PanelLeft className="size-3.5" />
            </div>
          </button>
        ) : (
          <button
            type="button"
            className="hidden aspect-square h-7 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-foreground/[0.04] focus-visible:bg-foreground/[0.04] md:flex"
            aria-label={t('sections.navigation.sidebarClose')}
            onClick={onToggle}
          >
            <PanelLeft className="size-3.5" />
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent side="right" align="center">
        {`${isCollapsed ? t('sections.navigation.sidebarShow') : t('sections.navigation.sidebarClose')} ${utils.getCommandKey()}/`}
      </TooltipContent>
    </Tooltip>
  );
};
